import React, { useState } from 'react';
import { useTheme } from '@mui/material';
import * as Sentry from '@sentry/react';
import DSBox from '../../../design-system/DSBox';
import GuideKlydoModal from './GuideKlydoModal';

export type TextLink = {
  text: string;
  url: string;
};

// A link to the public klydo route (`/public/klydo/<id>`) is previewed in a
// popup instead of opening a new tab. The nested collection variant
// (`/public/collection/<cid>/klydo/<kid>`) is intentionally not matched.
const KLYDO_URL_PATTERN = /\/public\/klydo\/([^/?#\s]+)/;

function getKlydoIdFromUrl(url: string): string | null {
  return url.match(KLYDO_URL_PATTERN)?.[1] ?? null;
}

type Segment =
  | { type: 'text'; content: string }
  | { type: 'link'; text: string; url: string };

function isSafeUrl(url: string): boolean {
  return /^https?:\/\//i.test(url.trim());
}

function parseSegments(text: string, links: TextLink[]): Segment[] {
  // The API validates link URLs, so an unsafe one here means bad stored data —
  // drop it (don't render) but report it so we can fix the source content.
  const safeLinks = links.filter((l) => {
    if (!l.text.trim()) return false;
    if (!isSafeUrl(l.url)) {
      Sentry.captureException(
        new Error(`Unsafe link URL in studio content: ${l.url}`),
        { extra: { linkText: l.text } },
      );
      return false;
    }
    return true;
  });
  let segments: Segment[] = [{ type: 'text', content: text }];

  for (const link of safeLinks) {
    const next: Segment[] = [];
    for (const seg of segments) {
      if (seg.type === 'link') {
        next.push(seg);
        continue;
      }
      const parts = seg.content.split(link.text);
      parts.forEach((part, i) => {
        if (part) next.push({ type: 'text', content: part });
        if (i < parts.length - 1)
          next.push({ type: 'link', text: link.text, url: link.url });
      });
    }
    segments = next;
  }

  return segments;
}

const renderText = (text: string) =>
  text.split('\n').map((line, i, arr) => (
    <React.Fragment key={`${i}-${line}`}>
      {line}
      {i < arr.length - 1 && <br />}
    </React.Fragment>
  ));

type LinkedTextProps = {
  text: string;
  links?: TextLink[];
};

const LinkedText = ({ text, links }: LinkedTextProps) => {
  const { palette } = useTheme();
  const [klydoId, setKlydoId] = useState<string | null>(null);

  if (!links?.length) {
    return <>{renderText(text)}</>;
  }

  const handleLinkClick = (url: string) => {
    const id = getKlydoIdFromUrl(url);
    if (id) {
      setKlydoId(id);
    } else {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <>
      {parseSegments(text, links).map((seg, i) =>
        seg.type === 'link' ? (
          <DSBox
            key={`${i}-${seg.text}`}
            component="span"
            sx={{
              color: palette.primary.main,
              textDecoration: 'underline',
              cursor: 'pointer',
              '&:hover': { color: palette.primary.light },
            }}
            onClick={() => handleLinkClick(seg.url)}
          >
            {seg.text}
          </DSBox>
        ) : (
          <React.Fragment key={`${i}-${seg.content}`}>
            {renderText(seg.content)}
          </React.Fragment>
        ),
      )}
      {klydoId && (
        <GuideKlydoModal
          klydoId={klydoId}
          open
          onClose={() => setKlydoId(null)}
        />
      )}
    </>
  );
};

export default LinkedText;
